import { useCallback, useEffect, useState } from "react";
import {
  widgetAttachmentName,
  type WidgetAttachment,
} from "./widget-attachments.js";

/** Explicit widget asks are independent from transient canvas selection. */
export function useWidgetAttachments(workspaceId: string | undefined) {
  const [pending, setPending] = useState<WidgetAttachment[]>([]);
  useEffect(() => {
    setPending([]);
  }, [workspaceId]);
  useEffect(() => {
    const attach = (event: Event) => {
      const detail: unknown = (event as CustomEvent).detail;
      if (!detail || typeof detail !== "object" || !workspaceId) return;
      const value = detail as Record<string, unknown>;
      if (
        value.solveId !== workspaceId ||
        typeof value.widgetId !== "string" ||
        !value.widgetId ||
        typeof value.widgetKind !== "string"
      )
        return;
      const widgetId = value.widgetId,
        kind = value.widgetKind;
      setPending((current) => {
        if (current.some((item) => item.reference.widget_id === widgetId))
          return current;
        if (current.length >= 20) return current;
        return [
          ...current,
          {
            id: crypto.randomUUID(),
            name: widgetAttachmentName(kind),
            reference: {
              type: "widget",
              widget_id: widgetId,
              widget_kind: kind,
              scope_ref: {
                module_id: "solve",
                surface_type: "solve_canvas",
                surface_id: workspaceId,
              },
            },
          },
        ];
      });
    };
    window.addEventListener("playerai:open", attach);
    return () => window.removeEventListener("playerai:open", attach);
  }, [workspaceId]);
  const consume = useCallback((ids: readonly string[]) => {
    setPending((items) => items.filter((item) => !ids.includes(item.id)));
  }, []);
  // Filter synchronously too, before the scope-change effect runs.
  return {
    pending: pending.filter(
      (item) => item.reference.scope_ref.surface_id === workspaceId,
    ),
    consume,
    notice:
      pending.length >= 20
        ? "Maximum 20 widgets attached. Remove one to attach another."
        : "",
  };
}
